import { lumi } from "../lib/lumi"
import { useState, useEffect } from "react"
import { useAuth } from "../hooks/useAuth"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from "recharts"

type TimeEntry = {
  _id: string
  userId: string
  userName: string
  teamId: string
  teamName: string
  projectId: string
  projectName: string
  projectCategory: string
  weekStart: string
  weekEnd: string
  dailyEntries: DailyEntry[]
  totalHours: number
  status: string
  isTemporary: boolean
  createdAt: string
}

type DailyEntry = {
  date: string
  dayOfWeek: string
  isHoliday: boolean
  vacationType: string
  activities: Activity[]
}

type Activity = {
  activityCode: string
  hours: number
  tags: string[]
}

type UserProfile = {
  _id: string
  userId: string
  userName: string
  teamId: string
  teamName: string
}

export function TeamDashboard() {
  const { user, teamId, teamName } = useAuth()
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([])
  const [teamMembers, setTeamMembers] = useState<UserProfile[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedMember, setSelectedMember] = useState<string>("all")
  const [startDate, setStartDate] = useState<string>("")
  const [endDate, setEndDate] = useState<string>("")
  
  // 프로젝트별 그래프 선택 상태
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set())
  
  // 직원별 그래프 선택 상태
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadTeamData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, startDate, endDate])

  const loadTeamData = async () => {
    if (!teamId) return
    
    try {
      setLoading(true)

      // 2단계 최적화: time_entries2 서버 사이드 필터
      // - teamId + draft(임시저장) 제외를 서버에서 처리
      // - 기간(weekStart) 범위를 서버에서 제한하여 전송량 감소
      const entryFilter: Record<string, any> = { teamId, status: { $ne: "draft" } }
      if (startDate || endDate) {
        const weekStartCond: Record<string, any> = {}
        if (startDate) {
          // 주 단위 겹침을 고려해 시작일에서 7일 여유를 둔다
          const s = new Date(startDate)
          s.setDate(s.getDate() - 7)
          weekStartCond.$gte = s.toISOString()
        }
        if (endDate) {
          const e = new Date(endDate)
          e.setHours(23, 59, 59, 999)
          weekStartCond.$lte = e.toISOString()
        }
        entryFilter.weekStart = weekStartCond
      }
      
      // 같은 팀의 팀원 목록 + 투입시간 데이터를 병렬 조회
      const [membersResult, entriesResult] = await Promise.all([
        lumi.entities.userProfiles3.list({
          filter: { teamId, enabled: true },
          limit: 1000
        }),
        lumi.entities.time_entries2.list({
          filter: entryFilter,
          limit: 5000
        })
      ])
      
      setTeamMembers(membersResult.list as UserProfile[])
      setTimeEntries(entriesResult.list as TimeEntry[])
    } catch (error) {
      console.error("팀 데이터 로딩 실패:", error)
      alert("❌ 데이터 로딩에 실패했습니다.")
    } finally {
      setLoading(false)
    }
  }

  // 필터링된 데이터 (연차 제외 + 기간 필터 + 임시저장 제외)
  const filteredEntries = timeEntries.filter((entry) => {
    if (selectedMember !== "all" && entry.userId !== selectedMember) return false
    // 연차 관련 프로젝트 제외
    if (entry.projectName === "연차" || entry.projectName === "오전반차" || entry.projectName === "오후반차" || entry.projectName === "반반차") return false
    // 임시저장(draft) 상태 제외
    if (entry.status === "draft") return false
    
    // 기간 필터링 적용
    if (startDate || endDate) {
      const entryStart = new Date(entry.weekStart)
      const entryEnd = new Date(entry.weekEnd)
      
      if (startDate) {
        const filterStart = new Date(startDate)
        // 입력 기간의 종료일이 필터 시작일보다 이전이면 제외
        if (entryEnd < filterStart) return false
      }
      
      if (endDate) {
        const filterEnd = new Date(endDate)
        // 입력 기간의 시작일이 필터 종료일보다 이후이면 제외
        if (entryStart > filterEnd) return false
      }
    }
    
    return true
  })

  // 통계 계산
  const stats = {
    totalProjects: new Set(filteredEntries.map(e => e.projectId)).size,
    totalHours: filteredEntries.reduce((sum, e) => sum + (e.totalHours || 0), 0),
    activeMembers: new Set(filteredEntries.map(e => e.userId)).size,
    thisWeekHours: filteredEntries
      .filter(e => {
        const weekStart = new Date(e.weekStart)
        const now = new Date()
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        return weekStart >= weekAgo
      })
      .reduce((sum, e) => sum + (e.totalHours || 0), 0)
  }

  // 프로젝트별 투입시간 집계 (활동코드별 포함)
  const projectStats = filteredEntries.reduce((acc, entry) => {
    const key = entry.projectId
    if (!acc[key]) {
      acc[key] = {
        projectName: entry.projectName,
        projectCategory: entry.projectCategory,
        totalHours: 0,
        activityBreakdown: {} as Record<string, number>
      }
    }
    acc[key].totalHours += entry.totalHours || 0
    
    // 활동코드별 시간 집계
    entry.dailyEntries?.forEach((day) => {
      day.activities?.forEach((activity) => {
        if (!acc[key].activityBreakdown[activity.activityCode]) {
          acc[key].activityBreakdown[activity.activityCode] = 0
        }
        acc[key].activityBreakdown[activity.activityCode] += activity.hours || 0
      })
    })
    
    return acc
  }, {} as Record<string, { projectName: string; projectCategory: string; totalHours: number; activityBreakdown: Record<string, number> }>)

  const projectList = Object.values(projectStats).sort((a, b) => b.totalHours - a.totalHours)

  // 초기 로드 시 상위 10개 프로젝트 선택
  useEffect(() => {
    if (projectList.length > 0 && selectedProjects.size === 0) {
      const top10 = new Set(projectList.slice(0, 10).map((_, idx) => idx.toString()))
      setSelectedProjects(top10)
    }
  }, [projectList.length])

  // 활동 코드별 투입시간 집계
  const activityStats = filteredEntries.reduce((acc, entry) => {
    entry.dailyEntries?.forEach((day) => {
      day.activities?.forEach((activity) => {
        if (!acc[activity.activityCode]) {
          acc[activity.activityCode] = 0
        }
        acc[activity.activityCode] += activity.hours || 0
      })
    })
    return acc
  }, {} as Record<string, number>)

  const activityList = Object.entries(activityStats)
    .map(([code, hours]) => ({ activityCode: code, hours }))
    .sort((a, b) => b.hours - a.hours)

  // 팀원별 투입시간 집계
  const memberStats = filteredEntries.reduce((acc, entry) => {
    const key = entry.userId
    if (!acc[key]) {
      acc[key] = {
        userId: entry.userId,
        userName: entry.userName,
        totalHours: 0,
        projectCount: new Set()
      }
    }
    acc[key].totalHours += entry.totalHours || 0
    acc[key].projectCount.add(entry.projectId)
    return acc
  }, {} as Record<string, { userId: string; userName: string; totalHours: number; projectCount: Set<string> }>)

  const memberList = Object.values(memberStats).map(member => ({
    ...member,
    projectCount: member.projectCount.size
  })).sort((a, b) => b.totalHours - a.totalHours)

  // 카테고리별 색상
  const getCategoryColor = (category: string) => {
    switch (category) {
      case "납품장비": return "bg-blue-100 text-blue-700"
      case "제품개발": return "bg-green-100 text-green-700"
      case "연구개발": return "bg-purple-100 text-purple-700"
      case "경영지원": return "bg-orange-100 text-orange-700"
      default: return "bg-gray-100 text-gray-700"
    }
  }

  // 활동 코드별 색상 (Recharts용)
  const getActivityColor = (code: string) => {
    const colors: Record<string, string> = {
      "개발/설계": "#3B82F6",
      "영업/견적/PM/기획": "#10B981",
      "제작/조립/구매": "#EAB308",
      "시험/검증/테스트": "#EF4444",
      "설치/납품/AS": "#A855F7",
      "지원/문서/교육": "#EC4899",
      "미팅": "#6366F1",
      "이동": "#14B8A6"
    }
    return colors[code] || "#6B7280"
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">팀 대시보드</h2>
        <p className="text-gray-600">{teamName} 팀의 프로젝트별, 활동별 투입 시간을 확인하세요</p>
      </div>

      {/* 필터 영역 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        {/* 첫 번째 줄: 팀원, 새로고침 버튼 */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 lg:gap-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-2 flex-1">
            <label className="text-sm font-medium text-gray-700 lg:whitespace-nowrap">팀원:</label>
            <select
              value={selectedMember}
              onChange={(e) => setSelectedMember(e.target.value)}
              className="w-full lg:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
            >
              <option value="all">전체 팀원</option>
              {teamMembers.map((member) => (
                <option key={member._id} value={member.userId}>
                  {member.userName}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={loadTeamData}
            disabled={loading}
            className="w-full lg:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          >
            {loading ? "로딩 중..." : "🔄 새로고침"}
          </button>
        </div>

        {/* 기간 선택 */}
        <div className="border-t border-gray-200 pt-4 mt-4">
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">기간:</label>
            <div className="flex items-center gap-2 flex-1">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
              />
              <span className="text-gray-500">~</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
              />
            </div>
            {(startDate || endDate) && (
              <button
                onClick={() => {
                  setStartDate("")
                  setEndDate("")
                }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                🔄 기간 초기화
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">총 프로젝트</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalProjects}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">총 투입 시간</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalHours}h</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">활동 팀원</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.activeMembers}명</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">이번 주</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.thisWeekHours}h</p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* 프로젝트별 누적 막대 그래프 (Stacked Bar Chart) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">프로젝트별 투입 시간 (활동코드별 누적)</h3>
        {projectList.length === 0 ? (
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <p className="text-gray-500">데이터가 없습니다</p>
          </div>
        ) : (
          <>
            {/* 프로젝트 선택 체크박스 */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">표시할 프로젝트 선택:</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const allProjects = new Set(projectList.map((_, idx) => idx.toString()))
                      setSelectedProjects(allProjects)
                    }}
                    className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                  >
                    전체선택
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => {
                      setSelectedProjects(new Set())
                    }}
                    className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                  >
                    전체해제
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => {
                      const top10 = new Set(projectList.slice(0, 10).map((_, idx) => idx.toString()))
                      setSelectedProjects(top10)
                    }}
                    className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                  >
                    상위 10개 선택
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                {projectList.map((project, idx) => (
                  <label key={idx} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedProjects.has(idx.toString())}
                      onChange={(e) => {
                        const newSelected = new Set(selectedProjects)
                        if (e.target.checked) {
                          newSelected.add(idx.toString())
                        } else {
                          newSelected.delete(idx.toString())
                        }
                        setSelectedProjects(newSelected)
                      }}
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="text-gray-700 truncate" title={project.projectName}>
                      {project.projectName}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* 그래프 */}
            <div className="h-[576px] sm:h-[800px] md:h-[576px]">
              <ResponsiveContainer width="100%" height="100%">
                {/* 모바일: 가로 막대 그래프 (horizontal) */}
                <div className="block md:hidden h-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={projectList
                        .filter((_, idx) => selectedProjects.has(idx.toString()))
                        .map(p => ({
                          name: p.projectName.length > 12 ? p.projectName.substring(0, 12) + '...' : p.projectName,
                          fullName: p.projectName,
                          ...p.activityBreakdown
                        }))}
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" label={{ value: '시간 (h)', position: 'insideBottom', offset: -5 }} />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        width={0}
                        tick={false}
                      />
                      <Tooltip 
                        labelFormatter={(label, payload) => {
                          if (payload && payload[0]) {
                            return payload[0].payload.fullName
                          }
                          return label
                        }}
                      />
                      <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '10px' }} />
                      <Bar dataKey="개발/설계" stackId="a" fill="#3B82F6" name="개발/설계" />
                      <Bar dataKey="영업/견적/PM/기획" stackId="a" fill="#10B981" name="영업/견적/PM/기획" />
                      <Bar dataKey="제작/조립/구매" stackId="a" fill="#EAB308" name="제작/조립/구매" />
                      <Bar dataKey="시험/검증/테스트" stackId="a" fill="#EF4444" name="시험/검증/테스트" />
                      <Bar dataKey="설치/납품/AS" stackId="a" fill="#A855F7" name="설치/납품/AS" />
                      <Bar dataKey="지원/문서/교육" stackId="a" fill="#EC4899" name="지원/문서/교육" />
                      <Bar dataKey="미팅" stackId="a" fill="#6366F1" name="미팅" />
                      <Bar dataKey="이동" stackId="a" fill="#14B8A6" name="이동" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* 데스크톱: 세로 막대 그래프 (vertical) */}
                <div className="hidden md:block h-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={projectList
                        .filter((_, idx) => selectedProjects.has(idx.toString()))
                        .map(p => ({
                          name: p.projectName.length > 15 ? p.projectName.substring(0, 15) + '...' : p.projectName,
                          fullName: p.projectName,
                          ...p.activityBreakdown
                        }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} style={{ fontSize: '12px' }} />
                      <YAxis label={{ value: '시간 (h)', angle: -90, position: 'insideLeft' }} />
                      <Tooltip 
                        labelFormatter={(label, payload) => {
                          if (payload && payload[0]) {
                            return payload[0].payload.fullName
                          }
                          return label
                        }}
                      />
                      <Legend wrapperStyle={{ paddingTop: '20px' }} />
                      <Bar dataKey="개발/설계" stackId="a" fill="#3B82F6" name="개발/설계" />
                      <Bar dataKey="영업/견적/PM/기획" stackId="a" fill="#10B981" name="영업/견적/PM/기획" />
                      <Bar dataKey="제작/조립/구매" stackId="a" fill="#EAB308" name="제작/조립/구매" />
                      <Bar dataKey="시험/검증/테스트" stackId="a" fill="#EF4444" name="시험/검증/테스트" />
                      <Bar dataKey="설치/납품/AS" stackId="a" fill="#A855F7" name="설치/납품/AS" />
                      <Bar dataKey="지원/문서/교육" stackId="a" fill="#EC4899" name="지원/문서/교육" />
                      <Bar dataKey="미팅" stackId="a" fill="#6366F1" name="미팅" />
                      <Bar dataKey="이동" stackId="a" fill="#14B8A6" name="이동" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>

      {/* 활동 코드별 투입시간 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">활동 코드별 투입시간</h3>
        {activityList.length === 0 ? (
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <p className="text-gray-500">데이터가 없습니다</p>
          </div>
        ) : (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={activityList} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="activityCode" 
                  angle={-45} 
                  textAnchor="end" 
                  height={80}
                  style={{ fontSize: '12px' }}
                />
                <YAxis label={{ value: '시간 (h)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Bar dataKey="hours" name="투입시간">
                  {activityList.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getActivityColor(entry.activityCode)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* 팀원별 활동 현황 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">팀원별 활동 현황</h3>
        {memberList.length === 0 ? (
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <p className="text-gray-500">데이터가 없습니다</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">팀원</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">참여 프로젝트</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">총 투입시간</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">비율</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {memberList.map((member, index) => {
                  const percentage = stats.totalHours > 0 ? (member.totalHours / stats.totalHours) * 100 : 0
                  return (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{member.userName}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{member.projectCount}개</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">{member.totalHours}h</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{percentage.toFixed(1)}%</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
